<script setup lang="tsx">
import IconButton from '@/components/shared/IconButton.vue'
import LoadingContainer from '@/components/widgets/LoadingContainer.vue'
import { type RiskCatalogMeta } from '@/models/api-generated'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { v4 as uuidv4 } from 'uuid'
import { riskConfig } from '../riskConfig'
import { newDefaultCatalog } from '../shared'

const props = defineProps<{
	isEdit?: boolean
}>()

const isLoading = ref(false)
const model = defineModel<RiskCatalogMeta[]>({ required: true })
const deletedCatalogIds = defineModel<Set<number>>('deletedCatalogIds', { required: true })
const { t } = useI18n()

const removeNew = (index: number) => {
	model.value.splice(index, 1)
}

const toggleDeleted = (id: number) =>
	deletedCatalogIds.value.has(id) ? deletedCatalogIds.value.delete(id) : deletedCatalogIds.value.add(id)

const newCatalogRow = () => {
	const row = newDefaultCatalog()

	model.value.push(row)
}

const tempKeys = new WeakMap<RiskCatalogMeta, string>()

const rowKey = (c: RiskCatalogMeta) => {
	if (c.id) return c.id
	let key = tempKeys.get(c)
	if (!key) {
		key = uuidv4()
		tempKeys.set(c, key)
	}
	return key
}
</script>

<template>
	<div class="card p-3 m-3">
		<div class="row mb-2">
			<div class="col">
				<h3>{{ $t('risk.catalog', 2) }}</h3>
				<LoadingContainer v-model="isLoading" class="mt-3 p-3">
					<div class="table-responsive w-100">
						<table class="table">
							<thead>
								<tr>
									<th>{{ t('risk.name') }}</th>
									<th>{{ t('active') }}</th>
									<th>{{ t('risk.custom') }}</th>
									<th>{{ t('risk.risk', 1) }}</th>
									<th>{{ t('risk.threat', 2) }}</th>
									<th>{{ t('risk.vulnerability', 2) }}</th>
									<th></th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="(c, i) in model" :key="rowKey(c)" :class="{ 'table-danger': deletedCatalogIds.has(c.id) }">
									<td class="align-middle">
										<input
											v-if="(c.catalog.isCustom && props.isEdit) || !c.id"
											v-model="c.catalog.name"
											:data-test="'catalog-name-input-' + i"
											type="text"
											class="form-control w-75"
										/>
										<span v-else>{{ c.catalog.name }}</span>
									</td>
									<td>
										<div v-if="props.isEdit" class="form-check form-switch">
											<input
												class="form-check-input"
												type="checkbox"
												role="switch"
												:data-test="'catalog-active-' + i"
												v-model="model[i].configId"
												:true-value="riskConfig.id"
												:false-value="undefined"
											/>
										</div>
										<template v-else>
											{{ c.configId ? t('misc.yes') : t('misc.no') }}
										</template>
									</td>
									<td>{{ c.catalog.isCustom ? t('misc.yes') : t('misc.no') }}</td>
									<td>{{ c.numRisks.toFixed() }}</td>
									<td>{{ c.numThreats.toFixed() }}</td>
									<td>{{ c.numVulnerabilities.toFixed() }}</td>
									<td class="text-end">
										<IconButton
											v-if="c.catalog.isCustom && props.isEdit"
											:data-test="'catalog-delete-toggle' + i"
											class="bi bi-trash text-danger"
											@click="c.id ? toggleDeleted(c.id) : removeNew(i)"
											:title="$t('misc.delete')"
											size="fs-5"
										/>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</LoadingContainer>
				<div class="d-flex justify-content-center" v-if="props.isEdit">
					<IconButton data-test="add-new-catalog" :title="t('misc.add')" class="bi bi-plus-circle" @click="newCatalogRow" />
				</div>
			</div>
		</div>
	</div>
</template>

<style lang="sass" scoped></style>
